/**
 * Cross-field checks on the per-source emission settings — what the form schema
 * cannot express, since it validates one field at a time.
 *
 * `sessionOptions.video.googleBitrates` is the dangerous one: three independent
 * kbps fields, written as `x-google-*-bitrate` into every video format of the
 * offer received from OvenMedia. An inverted min/max passes every layer of the
 * app untouched, then the browser refuses the send parameters and the
 * publication dies during negotiation — reported as a closed peer connection,
 * far from its cause. So it is refused at save time instead.
 */

/** kbps triplet, as the SDK writes it into the video fmtp lines. */
interface GoogleBitrates {
  /** `false` means "write nothing", so only a number is comparable. */
  start?: number | false;
  min?: number;
  max?: number;
}

export interface EmissionSettingsError {
  /** Dotted path of the offending field, e.g. `sources[0].emission…`. */
  path: string;
  message: string;
}

const isNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

const validateGoogleBitrates = (bitrates: GoogleBitrates | undefined, path: string): EmissionSettingsError[] => {
  if (!bitrates || typeof bitrates !== "object") return [];
  const { start, min, max } = bitrates;
  const broken = (isNumber(min) && isNumber(max) && min > max)
    || (isNumber(start) && isNumber(max) && start > max)
    || (isNumber(start) && isNumber(min) && start < min);
  if (!broken) return [];
  // One line per field rather than per broken pair: an inverted triplet breaks
  // two or three rules at once, and the values say which one is off.
  const values = [
    isNumber(start) ? `start ${start}` : undefined,
    isNumber(min) ? `min ${min}` : undefined,
    isNumber(max) ? `max ${max}` : undefined,
  ].filter(Boolean).join(", ");
  return [{ path, message: `ordre min ≤ start ≤ max non respecté (${values}, en kbps)` }];
};

/** Emission settings of a single source, wherever that source is declared. */
const validateSource = (source: any, path: string): EmissionSettingsError[] => {
  const bitrates = source?.emission?.sessionOptions?.video?.googleBitrates;
  return validateGoogleBitrates(bitrates, `${path}.emission.sessionOptions.video.googleBitrates`);
};

/**
 * Every source of a settings object: the meeting sources plus the two feedback
 * ones, which share the same schema. Empty when nothing is inconsistent.
 */
export const validateEmissionSettings = (settings: any): EmissionSettingsError[] => {
  const errors: EmissionSettingsError[] = [];
  const sources = settings?.sources;
  if (Array.isArray(sources)) {
    sources.forEach((source, i) => errors.push(...validateSource(source, `sources[${i}]`)));
  }
  errors.push(...validateSource(settings?.videoFeedback?.source, "videoFeedback.source"));
  errors.push(...validateSource(settings?.webcamFeedback?.source, "webcamFeedback.source"));
  return errors;
};
